// ** Types
import { NavGroup, NavLink } from 'src/@core/layouts/types'
import { NextRouter } from 'next/router'

/**
 * Check for URL queries as well for matching
 * Current URL & Item Path
 *
 * @param item
 * @param activeItem
 */
export const handleURLQueries = (router: NextRouter, path: string | undefined): boolean => {
  if (Object.keys(router.query).length && path) {
    const arr = Object.keys(router.query)

    // Special handling for user management routes to prevent false matches
    if (path === '/app/user-management' && router.asPath.startsWith('/app/user-management/profile')) {
      return false
    }
    if (path === '/app/user-management' && router.asPath.startsWith('/app/user-management/list')) {
      return false
    }
    if (path === '/app/user-management' && router.asPath.startsWith('/app/user-management/changes')) {
      return false
    }

    return router.asPath.includes(path) && router.asPath.includes(router.query[arr[0]] as string) && path !== '/'
  }

  return false
}

/**
 * Check if the given item has the given url
 * in one of its children
 *
 * @param item
 * @param currentURL
 */
export const hasActiveChild = (item: NavGroup, currentURL: string): boolean => {
  const { children } = item

  if (!children) {
    return false
  }

  for (const child of children) {
    if ((child as NavGroup).children) {
      if (hasActiveChild(child, currentURL)) {
        return true
      }
    }
    const childPath = (child as NavLink).path

    // Check if the child has a link and is active
    if (
      child &&
      childPath &&
      currentURL &&
      (childPath === currentURL || (currentURL.includes(childPath) && childPath !== '/'))
    ) {
      return true
    }
  }

  return false
}

export const hasSubActiveChild = (item: NavGroup, currentURL: string, id?: string): boolean => {
  const { children } = item

  if (!children) {
    return false
  }

  for (const child of children) {
    if ((child as NavGroup).children) {
      if (hasSubActiveChild(child, currentURL, id)) {
        return true
      }
    }
    const childPath = (child as NavLink).path ? (child as NavLink).path : child?.path

    // Resolve navigation path, replacing [id] placeholder if valid
    const childPathData = resolveNavPath(childPath, id)

    // Check if the child has a link and is active
    if (
      child &&
      childPathData &&
      currentURL &&
      (childPath === currentURL || (currentURL.includes(childPathData) && childPathData !== '/'))
    ) {
      return true
    }
  }

  return false
}

/**
 * Check if this is a children
 * of the given item
 *
 * @param children
 * @param openGroup
 * @param currentActiveGroup
 */
export const removeChildren = (children: NavLink[], openGroup: string[], currentActiveGroup: string[]) => {
  children.forEach((child: NavLink) => {
    if (!currentActiveGroup.includes(child.title)) {
      const index = openGroup.indexOf(child.title)
      if (index > -1) openGroup.splice(index, 1)

      // @ts-ignore
      if (child.children) removeChildren(child.children, openGroup, currentActiveGroup)
    }
  })
}

/**
 * Resolve navigation path by replacing [id] placeholder with actual ID value
 * Only replaces [id] if the path contains it and the id is valid
 *
 * @param itemPath - The navigation item path (may contain [id] placeholder)
 * @param id - The ID value from router query (string, string[], number, or undefined)
 * @returns The resolved path string
 */
export const resolveNavPath = (itemPath: string | undefined, id: string | string[] | number | undefined): string => {
  if (!itemPath) return '/'

  // Normalize id - if it's an array, take the first element
  const normalizedId = Array.isArray(id) ? id[0] : id

  // Only replace [id] if:
  // 1. The item.path contains [id]
  // 2. The id is actually defined and valid (not null/undefined or empty)
  // This ensures we don't replace [id] with undefined/null when navigating from sub-navigation to main navigation
  const shouldReplaceId =
    itemPath.includes('[id]') &&
    normalizedId != null &&
    (typeof normalizedId === 'string' || typeof normalizedId === 'number') &&
    normalizedId !== ''

  return shouldReplaceId ? itemPath.replace('[id]', String(normalizedId)) : itemPath
}
